import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, Users, Trophy, Mail, Phone, Globe, Edit, Trash2, ExternalLink, FileDown, Percent } from 'lucide-react';
import { contacts as contactsApi } from '@/lib/api';
import { API } from '@/lib/api/_client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const KIND_LABEL = {
  referrer: 'Referrer', professional: 'Professional', sponsor_hr: 'Sponsor HR',
  agent: 'Migration agent', lawyer: 'Lawyer', accountant: 'Accountant', other: 'Other',
};
const KIND_TONE = {
  referrer: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  professional: 'border-blue-200 bg-blue-50 text-blue-700',
  sponsor_hr: 'border-amber-200 bg-amber-50 text-amber-700',
  agent: 'border-purple-200 bg-purple-50 text-purple-700',
  lawyer: 'border-slate-200 bg-slate-50 text-slate-700',
  accountant: 'border-slate-200 bg-slate-50 text-slate-700',
  other: 'border-slate-200 bg-slate-50 text-slate-700',
};

const money = (v) => `A$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ContactsPage() {
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [leaderboard, setLeaderboard] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [kindFilter, setKindFilter] = React.useState('all');
  const [q, setQ] = React.useState('');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [viewing, setViewing] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    const params = {};
    if (kindFilter !== 'all') params.kind = kindFilter;
    if (q) params.q = q;
    Promise.all([contactsApi.list(params), contactsApi.leaderboard(5)])
      .then(([lst, lb]) => { setItems(lst.items || []); setTotal(lst.total || 0); setLeaderboard(lb.items || []); })
      .catch(() => toast.error('Failed to load contacts'))
      .finally(() => setLoading(false));
  }, [kindFilter, q]);

  React.useEffect(load, [load]);

  const doDelete = async (id) => {
    if (!window.confirm('Delete this contact? They will be detached from any matters.')) return;
    try { await contactsApi.remove(id); toast.success('Contact deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="p-8" data-testid="contacts-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A3A5C]">Contacts CRM</h1>
          <p className="mt-1 text-sm text-slate-500">Referrers, professionals and sponsors — the people who feed the firm.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="contact-create-btn">
          <Plus className="mr-2 h-4 w-4" /> New contact
        </Button>
      </div>

      {leaderboard.length > 0 && (
        <Card className="mb-6" data-testid="contacts-leaderboard">
          <CardContent className="p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]"><Trophy className="h-4 w-4 text-amber-500" /> Top referrers by lifetime revenue</div>
            <div className="grid gap-2 md:grid-cols-5">
              {leaderboard.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => setViewing(r.id)}
                  className="rounded-lg border border-slate-200 bg-white p-3 text-left transition hover:border-emerald-300 hover:bg-emerald-50/40"
                  data-testid={`contacts-lb-${i}`}
                >
                  <div className="text-xs text-slate-400">#{i + 1}</div>
                  <div className="text-sm font-semibold text-[#1A3A5C]">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.matter_count} matters</div>
                  <div className="mt-1 font-serif text-lg font-bold text-emerald-700">{money(r.lifetime_revenue)}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, org, email" className="max-w-xs" data-testid="contacts-search" />
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="w-48" data-testid="contacts-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500">{total} contact{total === 1 ? '' : 's'}</div>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-slate-500">
          <Users className="mx-auto mb-2 h-10 w-10 text-slate-300" />
          No contacts yet. Add referrers and professionals to see who&rsquo;s feeding the firm.
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <Card key={c.id} data-testid={`contact-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="mb-2 flex items-start justify-between">
                  <div>
                    <div className="font-serif text-lg font-semibold text-[#1A3A5C]">{c.name}</div>
                    {c.org_name && <div className="text-xs text-slate-500">{c.org_name}</div>}
                  </div>
                  <Badge className={`${KIND_TONE[c.kind] || KIND_TONE.other} border`}>{KIND_LABEL[c.kind]}</Badge>
                </div>
                <div className="space-y-1 text-sm">
                  {c.email && <div className="flex items-center gap-2 text-slate-600"><Mail className="h-3 w-3" /> <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a></div>}
                  {c.phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-3 w-3" /> {c.phone}</div>}
                  {c.website && <div className="flex items-center gap-2 text-slate-600"><Globe className="h-3 w-3" /> <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="hover:underline">{c.website}</a></div>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-xs">
                  <div>
                    <div className="text-slate-400">Matters</div>
                    <div className="font-semibold text-[#1A3A5C]">{c.matter_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">Lifetime revenue</div>
                    <div className="font-semibold text-emerald-700">{money(c.lifetime_revenue)}</div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setViewing(c.id)} data-testid={`contact-view-${c.id}`}><ExternalLink className="mr-1 h-3 w-3" /> View</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(c)} data-testid={`contact-edit-${c.id}`}><Edit className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => doDelete(c.id)} data-testid={`contact-delete-${c.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ContactFormDialog
        open={createOpen || !!editing}
        contact={editing}
        onClose={() => { setCreateOpen(false); setEditing(null); }}
        onSaved={load}
      />
      <ContactDetailDialog
        contactId={viewing}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}

function ContactFormDialog({ open, contact, onClose, onSaved }) {
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (open) setForm(contact || { kind: 'referrer', name: '', org_name: '', email: '', phone: '', website: '', notes: '', marn: '' });
  }, [open, contact]);
  const submit = async () => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      // Strip blank emails so backend EmailStr doesn't reject
      if (!payload.email) delete payload.email;
      if (!payload.phone) delete payload.phone;
      if (!payload.website) delete payload.website;
      if (!payload.notes) delete payload.notes;
      if (!payload.marn) delete payload.marn;
      if (!payload.org_name) delete payload.org_name;
      if (payload.commission_pct === null || payload.commission_pct === undefined || payload.commission_pct === '') delete payload.commission_pct;
      if (contact) await contactsApi.update(contact.id, payload);
      else await contactsApi.create(payload);
      toast.success('Contact saved');
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>{contact ? 'Edit contact' : 'New contact'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Kind</label>
              <Select value={form.kind || 'referrer'} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="contact-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Name</label>
              <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Organisation</label>
            <Input value={form.org_name || ''} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="contact-form-org" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</label>
              <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-form-phone" />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Website</label>
              <Input value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">MARN (if agent)</label>
              <Input value={form.marn || ''} onChange={(e) => setForm({ ...form, marn: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Commission split (% of paid revenue)</label>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.5" min="0" max="100" value={form.commission_pct ?? ''} onChange={(e) => setForm({ ...form, commission_pct: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="10" data-testid="contact-form-commission" />
              <Percent className="h-4 w-4 text-slate-400" />
            </div>
            <div className="mt-1 text-xs text-slate-400">Defaults to 10% if left blank. Used on the referrer portal + monthly statements.</div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</label>
            <Textarea rows={3} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="contact-form-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactDetailDialog({ contactId, onClose }) {
  const [contact, setContact] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    contactsApi.get(contactId).then(setContact).finally(() => setLoading(false));
  }, [contactId]);
  return (
    <Dialog open={!!contactId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="contact-detail-dialog">
        <DialogHeader><DialogTitle>{contact?.name || 'Contact'}</DialogTitle></DialogHeader>
        {loading || !contact ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Badge className={`${KIND_TONE[contact.kind] || KIND_TONE.other} border`}>{KIND_LABEL[contact.kind]}</Badge>
              {contact.org_name && <span>· {contact.org_name}</span>}
              {contact.commission_pct !== undefined && contact.commission_pct !== null && (
                <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700"><Percent className="mr-1 h-3 w-3" />{contact.commission_pct}% split</Badge>
              )}
            </div>
            <MonthlyStatementButtons contactId={contact.id} />
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase text-slate-500">Matters referred</div>
                <div className="font-serif text-2xl font-bold text-[#1A3A5C]">{contact.revenue?.matter_count || 0}</div>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-xs uppercase text-slate-500">Lifetime revenue</div>
                <div className="font-serif text-2xl font-bold text-emerald-700">{money(contact.revenue?.lifetime_revenue)}</div>
              </div>
            </div>
            {contact.revenue?.matters?.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Referred matters</div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200">
                  {contact.revenue.matters.map((m) => (
                    <Link key={m.id} to={`/app/matters/${m.id}`} className="flex items-center justify-between border-b border-slate-50 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`contact-matter-${m.id}`}>
                      <div><span className="font-semibold text-[#2ECC9A]">{m.ref}</span> · {typeof m.applicant === 'string' ? m.applicant : m.applicant?.name || m.applicant_name}</div>
                      <div className="text-xs text-slate-500">{m.stage}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {contact.notes && (
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{contact.notes}</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MonthlyStatementButtons({ contactId }) {
  const now = new Date();
  const [year, setYear] = React.useState(now.getUTCFullYear());
  const [month, setMonth] = React.useState(now.getUTCMonth() + 1);
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await API.get(`/contacts/${contactId}/statement/${year}/${month}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission-${year}-${String(month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (e) {
      toast.error('Failed to build statement');
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="contact-statement-block">
      <Percent className="h-4 w-4 text-emerald-700" />
      <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800">Monthly statement</span>
      <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="h-8 w-20 text-xs" data-testid="contact-statement-year" />
      <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="contact-statement-month">
        {[...Array(12)].map((_, i) => (<option key={i + 1} value={i + 1}>{new Date(2000, i, 1).toLocaleString('en-AU', { month: 'long' })}</option>))}
      </select>
      <Button size="sm" onClick={download} disabled={busy} data-testid="contact-statement-download">
        {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <FileDown className="mr-1 h-3 w-3" />}Download PDF
      </Button>
    </div>
  );
}

